import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  Crosshair,
  Plus,
  AlertCircle,
  Camera,
  MapPin,
  Compass,
  Image,
  X,
  Sprout,
  Layers,
  Plane,
} from 'lucide-react'
import { getMonitoringPoints, createMonitoringPoint, getPaddocks } from '../../services/api'
import { format, parseISO, differenceInDays } from 'date-fns'
import StatCard from '../../components/StatCard'
import clsx from 'clsx'
import { useAuth } from '../../contexts/AuthContext'

const getPhotoBorderColor = (latestPhotoDate) => {
  if (!latestPhotoDate) return 'border-t-red-400'
  const days = differenceInDays(new Date(), parseISO(latestPhotoDate))
  if (days <= 14) return 'border-t-green-500'
  if (days <= 30) return 'border-t-amber-400'
  return 'border-t-red-400'
}

const getPhotoStatusBadge = (latestPhotoDate) => {
  if (!latestPhotoDate) return { color: 'bg-red-100 text-red-700', label: 'No photos' }
  const days = differenceInDays(new Date(), parseISO(latestPhotoDate))
  if (days <= 14) return { color: 'bg-green-100 text-green-700', label: 'Recent' }
  if (days <= 30) return { color: 'bg-amber-100 text-amber-700', label: `${days}d ago` }
  return { color: 'bg-red-100 text-red-700', label: `${days}d ago` }
}

export default function MonitoringPoints({ ranchId }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)
  const [paddockFilter, setPaddockFilter] = useState('')

  const { data: points, isLoading } = useQuery({
    queryKey: ['monitoringPoints', ranchId, paddockFilter],
    queryFn: () => getMonitoringPoints(ranchId, paddockFilter ? { paddock_id: paddockFilter } : {}),
  })

  const { data: paddocks } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })

  const totalPoints = points?.length || 0
  const totalPhotos = points?.reduce((sum, p) => sum + (p.photo_count || 0), 0) || 0
  const needingPhotos = points?.filter(p => {
    if (!p.latest_photo_date) return true
    return differenceInDays(new Date(), parseISO(p.latest_photo_date)) > 30
  }).length || 0

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Land Monitoring</h1>
      </div>

      {/* Field Data Collection Section */}
      <div className="card p-5">
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Field Data Collection</h2>
          <p className="text-xs text-gray-500 mt-0.5">Vegetation surveys, biomass measurements, and drone calibration</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/land/training-data"
            className="btn bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 flex items-center"
          >
            <Layers className="h-4 w-4 mr-2" />
            Vegetation Survey
          </Link>
          <Link
            to="/land/biomass"
            className="btn bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 flex items-center"
          >
            <Sprout className="h-4 w-4 mr-2" />
            Biomass Log
          </Link>
          <Link
            to="/land/drone-calibration"
            className="btn bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 flex items-center"
          >
            <Plane className="h-4 w-4 mr-2" />
            Drone Calibration
          </Link>
          <Link
            to="/land/drone-runs"
            className="btn bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 flex items-center"
          >
            <Plane className="h-4 w-4 mr-2" />
            Drone Runs
          </Link>
          <Link
            to="/land/polygons"
            className="btn bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 flex items-center"
          >
            <Layers className="h-4 w-4 mr-2" />
            Vegetation Polygons
          </Link>
        </div>
      </div>

      {/* Photo Monitoring Points Section Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Photo Monitoring Points</h2>
        <button
          onClick={() => setShowModal(true)}
          className="btn bg-green-600 text-white hover:bg-green-700 flex items-center text-sm"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Point
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Monitoring Points"
          value={totalPoints}
          icon={Crosshair}
          color="green"
        />
        <StatCard
          title="Total Photos"
          value={totalPhotos}
          icon={Camera}
          color="blue"
        />
        <StatCard
          title="Needing Photos"
          value={needingPhotos}
          subtitle={needingPhotos > 0 ? 'No photo in >30 days' : 'All points current'}
          icon={AlertCircle}
          color={needingPhotos > 0 ? 'amber' : 'green'}
        />
      </div>

      {/* Paddock Filter */}
      {paddocks?.length > 0 && (
        <div className="card p-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Paddock:</label>
            <select
              value={paddockFilter}
              onChange={e => setPaddockFilter(e.target.value)}
              className="input max-w-xs"
            >
              <option value="">All paddocks</option>
              {paddocks.map(p => (
                <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Points Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : !points?.length ? (
        <div className="card p-12 text-center">
          <Crosshair className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No monitoring points</h3>
          <p className="text-gray-500 mt-1">
            Set up fixed GPS points in your paddocks to track vegetation change with repeat photography
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="btn bg-green-600 text-white hover:bg-green-700 mt-4 inline-flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add First Point
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {points.map((point) => {
            const borderColor = getPhotoBorderColor(point.latest_photo_date)
            const badge = getPhotoStatusBadge(point.latest_photo_date)
            return (
              <div
                key={point.id}
                onClick={() => navigate(`/land/${point.id}`)}
                className={clsx(
                  'card border-t-4 p-5 cursor-pointer hover:shadow-md transition-shadow',
                  borderColor
                )}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{point.name}</h3>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                      {point.paddock_code}
                    </span>
                  </div>
                  <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', badge.color)}>
                    {badge.label}
                  </span>
                </div>

                {point.description && (
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">{point.description}</p>
                )}

                <div className="space-y-1.5 text-xs text-gray-400">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-3 w-3" />
                    {point.latitude.toFixed(4)}, {point.longitude.toFixed(4)}
                  </div>
                  {point.compass_bearing != null && (
                    <div className="flex items-center gap-1.5">
                      <Compass className="h-3 w-3" />
                      {point.compass_bearing}° bearing
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Image className="h-3 w-3" />
                    {point.photo_count} photo{point.photo_count !== 1 ? 's' : ''}
                    {point.latest_photo_date && (
                      <span className="ml-1">
                        — last {format(parseISO(point.latest_photo_date), 'dd MMM yyyy')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <CreatePointModal
          ranchId={ranchId}
          paddocks={paddocks || []}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['monitoringPoints'] })
            setShowModal(false)
          }}
        />
      )}
    </div>
  )
}

function CreatePointModal({ ranchId, paddocks, onClose, onSuccess }) {
  const { userId } = useAuth()
  const [formData, setFormData] = useState({
    paddock_id: '',
    name: '',
    description: '',
    latitude: '',
    longitude: '',
    compass_bearing: '',
  })
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: createMonitoringPoint,
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Failed to create point'),
  })

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!formData.paddock_id || !formData.name || !formData.latitude || !formData.longitude) {
      setError('Fill in all required fields')
      return
    }

    mutation.mutate({
      ranch_id: ranchId,
      paddock_id: formData.paddock_id,
      name: formData.name,
      description: formData.description || null,
      latitude: parseFloat(formData.latitude),
      longitude: parseFloat(formData.longitude),
      compass_bearing: formData.compass_bearing ? parseInt(formData.compass_bearing) : null,
      created_by: userId,
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Add Monitoring Point</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="label">Paddock *</label>
            <select
              value={formData.paddock_id}
              onChange={e => handleChange('paddock_id', e.target.value)}
              className="input w-full"
              required
            >
              <option value="">Select paddock</option>
              {paddocks.map(p => (
                <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Point Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => handleChange('name', e.target.value)}
              placeholder="e.g. A1-North Waterhole"
              className="input w-full"
              required
            />
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={e => handleChange('description', e.target.value)}
              placeholder="Landmark description to help relocate the spot..."
              className="input w-full"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">Latitude *</label>
              <input
                type="number"
                value={formData.latitude}
                onChange={e => handleChange('latitude', e.target.value)}
                placeholder="0.3475"
                className="input w-full"
                step="0.0001"
                required
              />
            </div>
            <div>
              <label className="label">Longitude *</label>
              <input
                type="number"
                value={formData.longitude}
                onChange={e => handleChange('longitude', e.target.value)}
                placeholder="32.584"
                className="input w-full"
                step="0.0001"
                required
              />
            </div>
            <div>
              <label className="label">Bearing</label>
              <input
                type="number"
                value={formData.compass_bearing}
                onChange={e => handleChange('compass_bearing', e.target.value)}
                placeholder="0-359"
                className="input w-full"
                min="0"
                max="359"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn bg-gray-100 text-gray-700 hover:bg-gray-200">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn bg-green-600 text-white hover:bg-green-700 flex items-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Crosshair className="h-4 w-4" />
                  Create Point
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
